import { useEffect, useState } from "react";
import { api, fmtEur, fmtMese, formatErr, COMPANY_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";

const num = (s) => Number(String(s).replace(/\s|€/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".")) || 0;
const txt = (v) => (v ? String(v).replace(".", ",") : "");

const CAMPI = [
  ["carte", "Costo carte DKV", "IVA inclusa"],
  ["q8", "Canone carte Q8", "imponibile, +IVA 22%"],
  ["uta", "Service fee + canone UTA", "imponibile, +IVA 22%"],
  ["tp", "Canone Telepass", "IVA inclusa"],
  ["tpdkv", "Commissione Telepass DKV", "imponibile, +IVA 22%"],
];

export const CostiCarte = ({ company, mese, writable, refresh }) => {
  const [d, setD] = useState(null);
  const [val, setVal] = useState({});
  const [busy, setBusy] = useState(false);
  const single = company && company !== "all";
  useEffect(() => {
    if (!single) { setD(null); return; }
    api.get("/costi-carte", { params: { company, mese } }).then(({ data }) => {
      setD(data); setVal(Object.fromEntries(CAMPI.map(([k]) => [k, txt(data.importi[k])])));
    });
  }, [company, mese, single, refresh]);
  const applica = async () => {
    setBusy(true);
    try {
      const importi = Object.fromEntries(CAMPI.map(([k]) => [k, num(val[k] || "")]));
      const { data } = await api.put("/costi-carte", { company, mese, importi });
      setD(data); toast.success(`Costi carte-dispositivi riportati in ${data.righe.length} conteggi`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const preview = d?.utilizzatori;
  return (
    <Card className="p-4 rounded-md space-y-3" data-testid="costi-carte-card">
      <div className="flex items-center gap-2">
        <CreditCard className="w-4 h-4" />
        <h2 className="font-heading font-bold">Costi carte-dispositivi · {fmtMese(mese)}</h2>
      </div>
      {!single ? <div className="text-sm text-muted-foreground" data-testid="costi-carte-scegli-azienda">Scegli un'azienda in alto per inserire e dividere i costi.</div> : d && <>
        <p className="text-xs text-muted-foreground">Scrivi i costi del mese di {COMPANY_LABELS[company]}: ognuno viene diviso in parti uguali solo tra i padroncini che nel mese hanno usato quel servizio (carte gasolio, Telepass, Telepass DKV) e riportato come una riga unica nel conteggio generale.</p>
        <div className="flex flex-wrap items-end gap-3">
          {CAMPI.map(([k, l, s]) => (
            <div key={k}><Label className="text-xs">{l} <span className="text-muted-foreground">({s})</span></Label>
              <Input className="h-8 w-48 font-mono" value={val[k] || ""} onChange={(e) => setVal({ ...val, [k]: e.target.value })} placeholder="0,00" disabled={!writable || d.locked} data-testid={`costi-carte-input-${k}`} /></div>
          ))}
          {writable && !d.locked && <Button size="sm" className="h-8" disabled={busy} onClick={applica} data-testid="costi-carte-applica">
            {busy ? "Riporto…" : d.applicato_at ? "Aggiorna nei conteggi" : "Dividi e riporta nei conteggi"}</Button>}
          {d.locked && <span className="text-xs text-muted-foreground">Mese chiuso</span>}
        </div>
        <div className="text-xs text-muted-foreground" data-testid="costi-carte-utilizzatori">Hanno usato carte DKV: {preview.carte} · Q8: {preview.q8} · UTA: {preview.uta} · Telepass: {preview.tp} · Telepass DKV: {preview.tpdkv}{d.applicato_at ? " · ripartizione applicata (se cambi le assegnazioni dei consumi, premi di nuovo Aggiorna)" : ""}</div>
        {d.righe.length > 0 && (
          <table className="autotrans-table w-full text-sm" data-testid="costi-carte-table">
            <thead><tr><th>Padroncino (conteggio generale)</th><th className="text-right">Carte DKV</th><th className="text-right">Q8</th><th className="text-right">UTA</th><th className="text-right">Canone Telepass</th><th className="text-right">Telepass DKV (ivato)</th><th className="text-right">Totale riga</th></tr></thead>
            <tbody>{d.righe.map((r) => (
              <tr key={r.padroncino_id} data-testid={`costi-carte-row-${r.padroncino_id}`}>
                <td><span className="font-mono text-xs mr-2">{r.codice}</span>{r.nome}</td>
                <td className="text-right font-mono">{fmtEur(r.carte)}</td><td className="text-right font-mono">{fmtEur(r.q8)}</td><td className="text-right font-mono">{fmtEur(r.uta)}</td><td className="text-right font-mono">{fmtEur(r.tp)}</td><td className="text-right font-mono">{fmtEur(r.tpdkv)}</td>
                <td className="text-right font-mono font-bold">{fmtEur(r.totale)}</td>
              </tr>))}</tbody>
          </table>
        )}
      </>}
    </Card>
  );
};
